import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface RecordApproverResponseInput {
  changeRequestId: string;
  parentId: string;
  levelId: string;
  approved: boolean;
  comment: string;
}

/**
 * tRPC implementation of recording the actor's decision on an approval level
 * (RSP-6087). Exported from the folder barrel per the mutation-wrapper
 * convention (see packages/web/CLAUDE.md), but consumers MUST call the
 * flag-gated `useUpsertApproverResponses` wrapper — never this hook
 * directly (greppable rule: no `useUpsertApproverResponsesTRPC` outside
 * `src/hooks/mutations/`). The tRPC service resolves the
 * actor's eligible approver slot(s) at the level SERVER-SIDE (1-to-1 with the
 * legacy Hasura `updateApproverResponses` action) before upserting; the
 * data-layer re-validates each slot via perm_v2_is_eligible_approver.
 *
 * Outcomes: an actor with NO eligible slot at the level (or whose slots are
 * already approved — approvals are final) is a successful NO-OP
 * (`affected_rows: 0`), matching the legacy action; `mutateAsync` only
 * rejects on real failures (stale/non-Pending CR, forged slot claims the
 * data-layer 403s, validation errors). Don't build UI affordances that
 * expect a rejection for mere ineligibility.
 */
export const useUpsertApproverResponsesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.changeRequest.recordApproverResponse.mutationOptions({
      // Mirror the Apollo path's evictField(cache, 'change_request_by_pk') —
      // refresh the per-parent CR feeds so the recorded response + any status
      // flip surface without a manual reload.
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.changeRequest.changeRequestsByParent.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.changeRequest.pendingChangeRequests.queryKey(),
        });
      },
    }),
  });

  return {
    recordApproverResponse: async (input: RecordApproverResponseInput) => {
      await mutation.mutateAsync(input);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
